import { Big } from 'big.js';
import { match } from 'ts-pattern';
import { datetime } from '~/lib/datettime';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import BreJob from '~/tenants/bre/model/BreJob';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { bre_print_speed, CONTENT_WRITING_PAY, CONTENT_WRITING_RATE } from '~/tenants/bre/performable/common';
import { BreDesignPay, BreDesignRate } from '~/tenants/bre/performable/print/design/BreDesignData';
import BreFlyersConfig, { FlyerPaper, FlyerSide } from '~/tenants/bre/performable/print/flyers/BreFlyersConfig';
import { BreFlyersPay, BreFlyersRate } from '~/tenants/bre/performable/print/flyers/BreFlyersData';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';
import BrePhotosConfig from '~/tenants/bre/performable/photos/BrePhotosConfig';
import { BrePrintSpeed } from '~/tenants/bre/data';

interface PaperTypeOptions {
  paper: FlyerPaper;
  sides: FlyerSide;
  no_free_uv?: boolean;
}

export default class BreFlyersJob extends BreJob<typeof BreFlyersConfig> {
  get performable(): typeof BreFlyersConfig {
    return BreFlyersConfig;
  }

  get configurable(): boolean {
    return true;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.PROPERTY_MKTG);
  }

  get assignee(): string | null {
    return BreProviderId.PRINT_TEAM.toString();
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Sides',
      value: this.metadata.sides,
      provider: true,
    });

    info.push({
      name: 'Count',
      value: this.metadata.count,
      provider: true,
    });

    info.push({
      name: 'Paper',
      value: this.paperType,
      provider: true,
    });

    info.push({
      name: 'Content Writing',
      value: this.metadata.content_writing ? 'Yes' : 'No',
      provider: true,
    });

    if (this.metadata.design) {
      info.push({
        name: 'Design',
        value: this.metadata.design,
        provider: true,
      });
    }

    info.push({
      name: 'Need By',
      value: datetime(this.metadata.need_by, this.order.location.timezone).toFormat('LLL dd, yyyy'),
      provider: true,
    });

    return info;
  }

  get dependencies(): string[] {
    return [BrePhotosConfig.id];
  }

  get paperType(): string {
    const { paper, sides, no_free_uv } = this.metadata;
    return BreFlyersJob.paperType({ paper, sides, no_free_uv });
  }

  get sidesText(): string {
    const { sides } = this.metadata;
    return BreFlyersJob.sidesText(sides);
  }

  static paperType({ paper, sides, no_free_uv }: PaperTypeOptions): string {
    const withUv = match(sides)
      .with('4', () => (!no_free_uv ? ' with free UV cover' : ''))
      .otherwise(() => ' with UV cover');

    return match(paper)
      .with('low_gloss', () => 'Low-Gloss')
      .with('heavy_gloss', () => `Heavy-gloss${withUv}`)
      .with('super_heavy_gloss', () => `Super heavy-gloss${withUv}`)
      .exhaustive();
  }

  static sidesText(sides: FlyerSide): string {
    return match(sides)
      .with('1', (one) => `${one} Page`)
      .otherwise(() => `${sides} Pages`);
  }

  isDelivery(): boolean {
    return true;
  }

  isPrint(): boolean {
    return true;
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreFlyersConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      sides: '1',
      count: '50',
      paper: 'low_gloss',
      need_by: datetime(this.order.created, this.order.timezone).toSQLDate(),
    };
  }

  get printSpeed() {
    return bre_print_speed(this.order, this.metadata.need_by);
  }

  revenueLines(): TenantJobLine[] {
    const { count, paper, sides, content_writing, design } = this.metadata;
    const lines: TenantJobLine[] = [];

    const standard = BreFlyersRate[BrePrintSpeed.STANDARD][sides]?.[paper]?.[count];
    const rush = BreFlyersRate[BrePrintSpeed.RUSH][sides]?.[paper]?.[count];

    if (standard) {
      lines.push({
        id: 'flyers_print',
        description: `${this.sidesText} ${this.paperType} (x${count})`,
        amount: new Big(standard),
        taxable: true,
      });

      if (this.printSpeed === BrePrintSpeed.RUSH && rush) {
        lines.push({
          id: 'flyers_same_day_fee',
          description: 'Rush Print Fee',
          taxable: true,
          amount: new Big(rush).minus(standard),
        });
      }
    }

    if (content_writing) {
      lines.push({
        id: 'content_writing',
        description: 'Content Writing',
        amount: new Big(CONTENT_WRITING_RATE),
      });
    }

    if (design) {
      const rate = BreDesignRate[design][`flyer_${sides}`];

      if (rate) {
        lines.push({
          id: 'flyer_design',
          description: `Flyer design (${design} layout)`,
          amount: new Big(rate),
        });
      }
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const { count, paper, sides, content_writing, design } = this.metadata;
    const lines: TenantJobLine[] = [];

    const standard = BreFlyersPay[BrePrintSpeed.STANDARD][sides]?.[paper]?.[count];
    const rush = BreFlyersPay[BrePrintSpeed.RUSH][sides]?.[paper]?.[count];

    if (standard) {
      lines.push({
        id: 'flyers_print',
        description: `${this.sidesText} ${this.paperType} (x${count})`,
        amount: new Big(standard),
      });

      if (this.printSpeed === BrePrintSpeed.RUSH && rush) {
        lines.push({
          id: 'flyers_same_day_fee',
          description: 'Same-day print fee',
          amount: new Big(rush).minus(standard),
        });
      }
    }

    if (content_writing) {
      lines.push({
        id: 'content_writing',
        description: 'Content Writing',
        amount: new Big(CONTENT_WRITING_PAY),
      });
    }

    if (design) {
      const pay = BreDesignPay[design][`flyer_${sides}`];

      if (pay) {
        lines.push({
          id: 'flyer_design',
          description: `Flyer design (${design} layout)`,
          amount: new Big(pay),
        });
      }
    }

    return lines;
  }
}
